import type { DiscountType, SalesType, VatType } from './money'

export type DocumentStatus = 'Draft' | 'Issued' | 'Void'
export type PaymentStatus = 'Unpaid' | 'Paid' | 'Partial' | 'Cancelled'

export interface SalesLine {
  id?: string
  line_no?: number
  item: string
  unit: string
  qty: string
  unit_price: string
  amount?: string
  price_list_item_id?: string | null
  /** Exchange rate (Decimal string) this line's price was converted at, and when; both null = not recorded. */
  applied_rate?: string | null
  applied_rate_at?: string | null
  /** Base price (document currency) and the two markups that make unit_price; all null = not recorded. */
  base_unit_price?: string | null
  customs_tax_pct?: string | null
  margin_pct?: string | null
}

/** Append-only; `payment_status`/`balance_due` are folded from these server-side, never asserted by the client. */
interface PaymentHistoryEntry {
  id: string
  kind: 'payment' | 'cleared' | 'cancelled'
  amount?: string
  paid_date?: string
  currency?: string
  note: string
  recorded_at: string
  recorded_by_id: string | null
  source: string
}

export interface SalesDocument {
  id: string
  sales_type: SalesType
  status: DocumentStatus
  payment_status: PaymentStatus | null
  document_no: string | null
  no_type: string
  client_registration_no: string
  registration_no: string

  client_id: string | null
  attention_to: string
  organization: string
  address: string
  email: string
  phone: string
  purchaser: string

  document_date: string
  due_date: string | null
  paid_date: string | null
  issued_at: string | null

  currency: string
  /** Rate applied to prices imported from the price list. ⛔ `null` means native currency (no conversion), which is not `1`.
   *  A string, not a number: the column is Numeric(18, 6) and Decimal serialises quoted, so never do arithmetic on it directly. */
  exchange_rate: string | null
  /** Issue-time snapshot, never moves (unlike the live `exchange_rate`). `null` means no conversion happened, not parity. */
  fx: { rate: string; as_of: string | null } | null
  /** The live hand-typed MNT rate (tugriks per unit of `currency`), a string; ⛔ not `exchange_rate`. Optional for older fixtures. */
  mnt_rate?: string | null
  /** MNT equivalent frozen at issue; null on every draft and when no rate was set, then nothing is shown. */
  mnt?: { rate: string; total: string } | null
  discount_type: DiscountType
  discount: string
  vat_type: VatType
  /** Decimal places every amount on the document rounds to; optional for older fixtures, which read as 2. */
  rounding_places?: number
  /** The VAT registration number printed on this document, chosen per document rather than from the company identity. */
  vat_registration_no: string | null

  subtotal: string
  discount_value: string
  net: string
  vat: string
  total: string

  other_terms: string
  details: string
  template_version: string | null
  copy_of_id: string | null
  /** Which relation `copy_of_id` records; a bare string so an unrecognised value reads as unknown. Read via `lineageKind`. */
  lineage_kind: string | null
  /** Number of the revision whose issue voided this version; '' if unknown, null/absent when not superseded. */
  superseded_by_no?: string | null
  /** Newest live document that revised or converted this one. `replaced_by_no` null with an id set = replacement is still a draft. Read via `replacedBy`. */
  replaced_by_id?: string | null
  replaced_by_no?: string | null
  /** Read-only; null when the creator is unknown or nameless. Read via `creatorLabel`. */
  created_by_id?: string | null
  created_by_name?: string | null
  /** v1, v2, v3 along the revision chain; absent on an older response. */
  version?: number | null
  /** Server-side edit log; the last entry's `id` is the token a save sends as `expected_version_id`. */
  version_history?: { id?: string; action?: string; changes?: Record<string, unknown> }[]
  /** The draft's own issuing-company choice; `null` follows the client default, then the org default. */
  issuer_identity_id: string | null
  /** The chosen bank account, `null` = automatic. Optional so an older payload reads as automatic. */
  bank_account_id?: string | null
  /** Print without the signature. Optional so an older payload reads as signed. */
  hide_signature?: boolean
  /** Company an issued document was frozen under; null on drafts and imports means UNKNOWN, so show nothing, never a guess. */
  issued_as: { identity_id: string | null; name_en: string; name_mn: string; name_ru: string } | null
  /** Language the stored PDF was rendered in; null = not recorded. */
  issued_language?: 'mn' | 'en' | 'ru' | null
  created_at: string

  lines: SalesLine[]

  // Folded from `payment_history`, never sent by the client.
  payment_history: PaymentHistoryEntry[]
  amount_paid: string
  balance_due: string
}

export interface SalesDocumentListItem {
  id: string
  document_no: string | null
  sales_type: SalesType
  status: DocumentStatus
  payment_status: PaymentStatus | null
  organization: string
  document_date: string
  due_date: string | null
  paid_date: string | null
  currency: string
  total: string
  /** Absent on an older response, which reads as 2. */
  rounding_places?: number
  /** Newest live document that revised or converted this one; optional so an older cached response reads as not replaced. */
  replaced_by_id?: string | null
  replaced_by_no?: string | null
  /** Read-only; null when the creator is unknown or nameless. */
  created_by_id?: string | null
  created_by_name?: string | null
  /** Absent on an older response. */
  version?: number | null
}

/** One page of the sales list; `total` counts everything matching the filter, so a full page is not mistaken for a next page. */
export interface SalesDocumentPage {
  rows: SalesDocumentListItem[]
  total: number
}

interface ClientContact {
  id?: string
  name: string
  position: string
  email: string
  phone: string
  is_primary: boolean
}

export type ClientKind = 'company' | 'individual'

export interface Client {
  id: string
  name: string
  nickname: string
  // Changes what `registration_no` holds: a company's state number or an individual's personal register number.
  kind: ClientKind
  client_no: string | null
  registration_no: string
  organization: string
  address: string
  // NULL means no standing preference; the server then resolves the issuer identity and bank.
  default_issuer_identity_id: string | null
  default_bank_account_id: string | null
  // Changed only by the current account manager or an administrator; the server refuses anyone else.
  account_manager_id: string | null
  // Not shown yet: the lifecycle stages are still to be settled.
  status: string | null
  notes: string
  industry: string
  tags: string[]
  vat_no: string
  contacts: ClientContact[]
}

/** The slim picker-only shape, not the fuller admin shape. */
export interface IssuerIdentity {
  id: string
  slug: string
  name_en: string
  name_mn: string
  name_ru: string
  is_active: boolean
  is_default: boolean
}

/** Across all four identities; see `clientDefaults.ts::selectableBankAccounts` for the identity-scoped filter. */
export interface BankAccount {
  id: string
  issuer_identity_id: string
  bank_name: string
  account_name: string
  account_number: string
  currency: string
  is_default: boolean
  /** Printed by the new-style invoice; null falls back to the stored names. */
  bank_name_en: string | null
  account_name_en: string | null
}

export interface PriceListItem {
  id: string
  product: string
  group: string
  subgroup: string
  licencing_tool: string
  type_of: string
  price_daily: string | null
  price_monthly: string | null
  price_quarterly: string | null
  /** The fifth tier, hand-entered: ⛔ deliberately not a percentage of `price_annual`. */
  price_semiannual: string | null
  price_annual: string | null
  note: string
  seequent_contact: string
  fsl_prod_code: string
  fsl_deal_code: string
  fsl_update: string | null
  is_active: boolean
  /** Optional: an older backend sends none and readers take missing as USD. */
  currency?: string
}

/** A plain string, not a union: levels are rows an admin can add to, so the set is known only at runtime. */
export type AccessTier = string

/** Permissions belong to a level, not to a person. */
export interface AccessLevel {
  id: string
  slug: AccessTier
  label: string
  abilities: string[]
  /** Ships with the app: cannot be deleted, and `admin` cannot be edited. */
  is_builtin: boolean
  /** Users on it: why a delete is refused. */
  user_count: number
  /** This organization's edited copy of a built-in: deleting it restores the built-in. */
  overridden?: boolean
}

export interface User {
  id: string
  email: string | null
  /** Derived on the server from first/last name; not the Microsoft display name. */
  display_name: string
  tier: AccessTier
  /** The level's own label; blank when it has none. Read through `levelName`. */
  tier_label?: string
  is_admin: boolean
  /** Effective abilities granted by this user's access level. */
  abilities: string[]
  /** Null on an ordinary request; the real administrator's id while they view the app as this person. */
  impersonated_by_id: string | null
  /** True only while a leaver is inside the 90-day payslip window: the API confines them to `GET /api/me/payslips*`, so App.tsx branches on it before the shell.
   *  Recomputed per request; optional because `/api/admin/users` rows never carry it. */
  is_leaver_confined?: boolean
  /** Sign-in username, or null; names a person who has no email. */
  username?: string | null
  /** Caller's roster role, resolved by the same function every roster route gates on. Optional: `/api/admin/users` rows never carry it; absent reads as no roster role. */
  roster_access?: RosterRoleAccess
  /** `/auth/me`: this person has a contractor log to enter, approve or read. Absent reads as false. */
  contractor_log?: boolean
  /** `/auth/me`: the Inventory types this person may write today, a live temp rep's included. Absent reads as none. */
  inventory_ticks?: string[]
  /** `/auth/me`: every Full Time employee and anyone appointed. Absent reads as false. */
  inventory_reader?: boolean
  /** `/auth/me`: this person's organization is a hosted customer. Absent reads as false. */
  is_customer?: boolean
  /** A password is set on this person. Optional: older fixtures omit it; absent reads as false. */
  has_password?: boolean
  /** An administrator set the password: all but a few routes answer 403 `password_change_required:` until the person picks their own. */
  password_must_change?: boolean
}

/** `manager` plans (the draft); `staff` file for their appointed projects and read the published roster filtered to them;
 *  `person` is a confined external who reads only their own days; `null` has no roster role. There is no `viewer`. */
export type RosterRole = 'manager' | 'staff' | 'person'

interface RosterRoleAccess {
  role: RosterRole | null
  /** Projects the role works in (empty for a manager and for nobody). */
  projects: string[]
  /** Sentence to show when the role is null or the scope is empty. */
  reason: string
  /** True for a roster manager; `canPlan` reads THIS, never the ability, since `roster.plan` off the roster plans nothing. Absent reads as false: fails closed. */
  manager?: boolean
  /** Projects this caller may file actuals for; a manager's `projects` stays empty (they plan every project). */
  files_for?: string[]
  /** May file and clear actual hours for everyone (a manager, or roster staff). Absent reads as false. */
  actual_editor?: boolean
  /** May plan and edit every roster tab: a ticked manager or an administrator. Absent reads as false. */
  plan_editor?: boolean
  /** May read the Schedule page read-only: roster staff and every internal member. Absent reads as false. */
  schedule?: boolean
}

/** A platform operator (TRG's own staff). ⛔ Deliberately not a field on `User`: an operator has no `person` row, no organization,
 *  abilities or tier, and `GET /api/auth/me` refuses them, so no `can(user, ...)` check applies to one. */
export interface PlatformOperator {
  id: string
  email: string
  display_name: string
}

/** One tenant as the control plane sees it; not the tenant's own `Organization` record, an operator is served counts, not contents. */
export interface PlatformOrganization {
  id: string
  slug: string
  name_en: string
  person_count: number
  admin_count: number
  entra_tenant_id: string | null
  is_customer: boolean
  deactivated_at: string | null
}

/** `tier` and `account_active` are null together and null means never had an account, NOT deactivated: test `=== false`, never falsiness. */
export interface PlatformPerson {
  id: string
  email: string
  display_name: string
  tier: AccessTier | null
  tier_label?: string
  account_active: boolean | null
}

/** Which population a person belongs to, decided by `GET /api/admin/users`. ⛔ Not derived here: the inputs are not on the wire.
 *  `unaffiliated` has neither subtype record and keeps the four a total partition of `person`. */
export type UserBucket = 'full_time' | 'staff_other' | 'external' | 'unaffiliated'

/** A row of the admin Users page; includes people with no account, so `email` and `tier` are nullable and `User` is spread minus them rather than extended.
 *  ⚠ Null `tier`/`is_active` mean no account, NOT deactivated: test `=== false` or use `accountState`, never falsiness. */
export interface AdminUser extends Omit<User, 'email' | 'tier'> {
  email: string | null
  tier: AccessTier | null
  is_active: boolean | null
  /** Decided on the server; see `UserBucket`. */
  bucket: UserBucket
  /** Entra B2B guest invitation state; `invite_redeemed_at` is stamped on the first sign-in that resolves to it. */
  invited_at: string | null
  invite_redeemed_at: string | null
  /** Entra's redemption link, for an admin to pass on when the invitation mail does not arrive. */
  invite_redeem_url: string | null
  /** `staff_info.work_type`, read-only; null means not set, never a silent drop. */
  work_type: string | null
  resigned: boolean
  /** From the roster resolver; null is no roster role. */
  roster_role: 'manager' | 'staff' | 'person' | null
  /** The stored per-person tick; an administrator is a manager without it. */
  roster_manager: boolean
  /** The person's stored Inventory appointments. */
  inventory_ticks: string[]
  /** Bound to a Microsoft account; such a person cannot also hold a password. */
  microsoft_linked: boolean
  /** Sign-in username, or null. An import key: the password dialog may fill a blank one, never change a set one. */
  username?: string | null
}

/** Carries `email`, unlike `ExternalUser`, so an edit can round-trip the current address without blanking it. */
export interface ExternalRecord {
  id: string
  name: string
  display_name?: string
  email: string | null
  company: string
  project: string
  shift_role: string
  /** Blank = none entered. */
  phone: string
  is_active: boolean
  above_member?: boolean
  microsoft_linked?: boolean
}

export interface InvitationResult {
  user: AdminUser
  redeem_url: string
  status: string
  /** False when the address already had an account and this was a resend. */
  account_created: boolean
}

export interface Ability {
  key: string
  label: string
  /** What ticking this ability also grants; shown ticked and locked. */
  implies?: string[]
}

/** Names the person and the header/value to send, rather than the client hard-coding a string. */
export interface ImpersonationStart {
  person_id: string
  email: string
  display_name: string
  tier: string | null
  header: string
  header_value: string
}

/** The CEO's and CFO's own signatures: held only from the person's own level, never implied by admin. */
export const SIGNOFF_ABILITIES: ReadonlySet<string> = new Set(['signoff.ceo', 'signoff.cfo'])

/** True if the user has the ability, from their role or an extra grant; admins implicitly have all but the sign-offs. */
export function can(user: User | undefined | null, ability: string): boolean {
  if (!user) return false
  const held = (user.abilities ?? []).includes(ability)
  return SIGNOFF_ABILITIES.has(ability) ? held : user.is_admin || held
}

/** True if the user holds any one of several abilities that each unlock the same thing. */
export function canAny(user: User | undefined | null, abilities: string[]): boolean {
  return abilities.some((a) => can(user, a))
}

export interface SalesVersion {
  id: string
  document_no: string | null
  sales_type: string
  status: string
  document_date: string
  issued_at: string | null
  currency: string
  total: string
  rounding_places?: number
  version: number
  superseded_by_no?: string | null
}

export interface ClientCurrencyFigures {
  currency: string
  outstanding: string
  current: string
  days_1_30: string
  days_31_60: string
  days_61_90: string
  days_90_plus: string
  no_due_date: string
  overdue: string
  revenue_net: string
  invoice_count: number
}

/** `GET /api/sales/client-summary`: one client's figures over the issued documents the caller may read, per currency. */
export interface ClientSummary {
  client_id: string
  last_document_date: string | null
  open_quotations: number
  currencies: ClientCurrencyFigures[]
}
